import { create, type StoreApi, type UseBoundStore } from 'zustand';
import { debounce } from '../lib/debounce';
import { loadSyncedTree, saveSyncedTree } from '../lib/storage/indexedDb';
import type { Profile, ProfileTheme, SyncedTree } from '../lib/types';
import { DEFAULT_THEME } from '../themes/registry';
import type { CustomTheme } from '../themes/types';

// Optional sync hook. The engine wires itself in at module load (see
// features/sync/engine.ts); when no engine has registered, setTree is
// a pure local-IDB flush — original M1–M5 behaviour.
type TreeChangeListener = (prev: SyncedTree | null, next: SyncedTree) => void;
let treeChangeListener: TreeChangeListener | null = null;

export function setTreeChangeListener(listener: TreeChangeListener | null): void {
  treeChangeListener = listener;
}

// Synced family-wide state. Hydrates from IndexedDB on boot, debounces local
// writes back to IndexedDB. The remote-sync layer (M6) will design how
// incoming events apply.
//
// See plan/01 D4 (Zustand + sync-aware middleware) and plan/02 §Write strategy.

const DEFAULT_FLUSH_DELAY_MS = 800;

export interface SyncedState {
  tree: SyncedTree | null;
  isHydrated: boolean;
  setTree: (tree: SyncedTree) => void;
}

export interface SyncedStoreOptions {
  // Debounce window for the IDB flush after a local setTree. Tests can lower
  // this to keep run time short.
  flushDelayMs?: number;
}

export interface SyncedStore {
  useStore: UseBoundStore<StoreApi<SyncedState>>;
  hydrate: () => Promise<void>;
  reset: () => void;
}

export function createSyncedStore(options: SyncedStoreOptions = {}): SyncedStore {
  const flushDelayMs = options.flushDelayMs ?? DEFAULT_FLUSH_DELAY_MS;

  const flushToIdb = debounce((tree: SyncedTree) => {
    saveSyncedTree(tree).catch((err) => {
      // TODO(M5/M6): wire this into a user-visible error state — IDB write
      // failure means we'll lose the in-memory delta on reload.
      console.error('saveSyncedTree failed', err);
    });
  }, flushDelayMs);

  const useStore = create<SyncedState>()((set, get) => ({
    tree: null,
    isHydrated: false,
    setTree: (tree) => {
      const prev = get().tree;
      set({ tree });
      flushToIdb(tree);
      treeChangeListener?.(prev, tree);
    },
  }));

  const hydrate = async () => {
    const tree = await loadSyncedTree();
    useStore.setState({ tree, isHydrated: true });
  };

  const reset = () => {
    flushToIdb.cancel();
    useStore.setState({ tree: null, isHydrated: false });
  };

  return { useStore, hydrate, reset };
}

// Default singleton for the running app. Tests should construct their own
// instance via createSyncedStore() — never the singleton — so a flush window
// from one test cannot leak into another.
const defaultStore = createSyncedStore();
export const useSyncedStore = defaultStore.useStore;
export const hydrateSyncedStore = defaultStore.hydrate;

// -------- Family custom-theme library helpers (tasks/46 §D4) ---------
//
// Selectors read the singleton store — they're what the app UI uses.
// Mutations are pure tree functions: callers pipe the result through
// `setTree()`, matching the existing routineMutations pattern.

const EMPTY_CUSTOM_THEMES: readonly CustomTheme[] = Object.freeze([]);

export function useCustomThemes(): readonly CustomTheme[] {
  return useSyncedStore((s) => s.tree?.customThemes ?? EMPTY_CUSTOM_THEMES);
}

export function useCustomTheme(id: string | null | undefined): CustomTheme | undefined {
  return useSyncedStore((s) => {
    if (!id) return undefined;
    return s.tree?.customThemes.find((c) => c.id === id);
  });
}

function stampTree(tree: SyncedTree, now: string): SyncedTree {
  return { ...tree, meta: { ...tree.meta, lastWriteAt: now } };
}

export function addCustomTheme(
  tree: SyncedTree,
  theme: CustomTheme,
  now: string = new Date().toISOString(),
): SyncedTree {
  return stampTree({ ...tree, customThemes: [...tree.customThemes, theme] }, now);
}

export function updateCustomTheme(
  tree: SyncedTree,
  id: string,
  patch: Partial<Omit<CustomTheme, 'id' | 'createdAt'>>,
  now: string = new Date().toISOString(),
): SyncedTree {
  let changed = false;
  const customThemes = tree.customThemes.map((c) => {
    if (c.id !== id) return c;
    changed = true;
    return { ...c, ...patch, updatedAt: now };
  });
  if (!changed) return tree;
  return stampTree({ ...tree, customThemes }, now);
}

// Delete a library entry and sweep every profile reference back to
// `DEFAULT_THEME.key`. Only profiles that actually held a reference get
// their `updatedAt` bumped so the sync engine doesn't rewrite untouched
// profile rows. Deleting an unknown id is a no-op (returns the input).
export function deleteCustomTheme(
  tree: SyncedTree,
  id: string,
  now: string = new Date().toISOString(),
): SyncedTree {
  const customThemes = tree.customThemes.filter((c) => c.id !== id);
  if (customThemes.length === tree.customThemes.length) return tree;

  const fallback = DEFAULT_THEME.key;
  const profiles = tree.profiles.map((p) => sweepProfileReferences(p, id, fallback, now));
  return stampTree({ ...tree, customThemes, profiles }, now);
}

function sweepProfileReferences(
  profile: Profile,
  targetId: string,
  fallback: string,
  now: string,
): Profile {
  const usesDefault = profile.theme.default === targetId;

  let perRoutineChanged = false;
  const rewrittenPerRoutine: Record<string, string> = {};
  if (profile.theme.perRoutine) {
    for (const [routineId, value] of Object.entries(profile.theme.perRoutine)) {
      if (value === targetId) {
        rewrittenPerRoutine[routineId] = fallback;
        perRoutineChanged = true;
      } else {
        rewrittenPerRoutine[routineId] = value;
      }
    }
  }

  if (!usesDefault && !perRoutineChanged) return profile;

  const nextTheme: ProfileTheme = {
    ...profile.theme,
    default: usesDefault ? fallback : profile.theme.default,
  };
  if (Object.keys(rewrittenPerRoutine).length > 0) {
    nextTheme.perRoutine = rewrittenPerRoutine;
  }
  return { ...profile, theme: nextTheme, updatedAt: now };
}
